/* EDM Cart Drawer Controller v4.0 — Clean Production Version */
(() => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  const inner = drawer.querySelector('[data-edm-drawer-inner]');
  const overlay = document.querySelector('[data-edm-cart-overlay]');
  const CLOSE_SELECTOR = '.drawer__close, [data-drawer-close]';
  let keydownHandler = null;

  // Check if mobile (no overlay needed)
  const isMobile = () => window.innerWidth <= 1023;

  /**
   * Öffnet den Cart Drawer
   * @param {HTMLElement} trigger - Element das den Drawer geöffnet hat (für Focus-Return)
   * 
   * Ablauf:
   * 1. Prüft ob Drawer bereits offen
   * 2. Setzt aria-expanded auf Trigger
   * 3. Entfernt hidden-Attribute
   * 4. Setzt Klassen für Animation
   * 5. Aktiviert Focus-Trap
   * 6. Registriert Escape-Key Handler
   */
  function open(trigger) {
    if (drawer.hasAttribute('data-edm-open')) return;

    if (trigger) {
      drawer._activeTrigger = trigger;
      try { trigger.setAttribute('aria-expanded', 'true'); } catch(_) {}
    }

    // Show drawer and overlay
    drawer.removeAttribute('hidden');
    if (!isMobile()) {
      overlay?.removeAttribute('hidden');
    }

    drawer.classList.add('animate');
    requestAnimationFrame(() => {
      drawer.setAttribute('data-edm-open', '');
      drawer.classList.add('active');
      drawer.setAttribute('open', ''); // For Dawn compatibility
      document.body.classList.add('overflow-hidden');

      // Focus management
      const focusEl = inner || drawer.querySelector('.drawer__close');
      if (window.trapFocus) {
        try { trapFocus(drawer, focusEl); } catch(_) {}
      } else {
        focusEl?.focus?.({ preventScroll: true });
      }

      // Escape key handler
      keydownHandler = (e) => { if (e.key === 'Escape') close(); };
      document.addEventListener('keydown', keydownHandler, true);
      
      });
  }

  /**
   * Cleanup nach dem Schließen des Drawers
   * 
   * Wird aufgerufen nachdem die Schließ-Animation abgeschlossen ist.
   * Setzt alle States zurück und gibt Focus an Trigger-Element zurück.
   */
  function afterClose() {
    overlay?.setAttribute('hidden', '');
    drawer.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');

    if (window.removeTrapFocus) {
      try { removeTrapFocus(drawer._activeTrigger); } catch(_) {}
    }
    try {
      drawer._activeTrigger?.setAttribute('aria-expanded', 'false');
      drawer._activeTrigger?.focus?.();
    } catch(_) {}

    if (keydownHandler) {
      document.removeEventListener('keydown', keydownHandler, true);
      keydownHandler = null;
    }
  }

  /**
   * Schließt den Cart Drawer
   * 
   * Startet die Schließ-Animation und ruft afterClose() auf wenn fertig.
   * Bei prefers-reduced-motion wird afterClose() sofort aufgerufen.
   */
  function close() {
    if (!drawer.hasAttribute('data-edm-open')) return;
    
    drawer.classList.remove('active');
    drawer.removeAttribute('data-edm-open');
    drawer.removeAttribute('open'); // For Dawn compatibility

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !inner) return afterClose();

    const onEnd = (ev) => {
      if (ev && ev.target !== inner) return;
      inner.removeEventListener('transitionend', onEnd);
      afterClose();
    };
    inner.addEventListener('transitionend', onEnd, { once: true });
    setTimeout(onEnd, 420); // Fallback timeout
  }

  // Event Listeners
  overlay?.addEventListener('click', close);
  drawer.querySelectorAll(CLOSE_SELECTOR).forEach(btn => btn.addEventListener('click', close));

  // Override native cart-drawer methods
  drawer.open = open;
  drawer.close = close;

  // Fallback: Listen for Dawn Theme's attribute changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName === 'open' || m.attributeName === 'class') {
        const hasOpen = drawer.hasAttribute('open');
        const hasActive = drawer.classList.contains('active');
        const hasEdmOpen = drawer.hasAttribute('data-edm-open');
        
        // If Dawn opened but we didn't, sync state
        if ((hasOpen || hasActive) && !hasEdmOpen) {
          drawer.removeAttribute('hidden');
          drawer.setAttribute('data-edm-open', '');
          if (!isMobile()) overlay?.removeAttribute('hidden');
        }
      }
    });
  });
  observer.observe(drawer, { attributes: true, attributeFilter: ['open', 'class'] });

  // Handle resize (hide overlay on mobile)
  window.addEventListener('resize', () => {
    if (drawer.hasAttribute('data-edm-open')) {
      if (isMobile()) {
        overlay?.setAttribute('hidden', '');
      } else {
        overlay?.removeAttribute('hidden');
      }
    }
  });

  // Cart Link Interceptor - Prevents navigation to /cart and opens drawer instead
  const CART_OPEN_SELECTOR = [
    'a[href="/cart"]',
    'a[href$="/cart"]',
    '[data-cart-trigger]',
    '.header__icon--cart',
    '#cart-icon-bubble a',
    'cart-icon-bubble a'
  ].join(', ');

  document.addEventListener('click', (e) => {
    const cartLink = e.target.closest(CART_OPEN_SELECTOR);
    if (!cartLink) return;
    e.preventDefault();
    e.stopPropagation();
    open(cartLink);
  }, true);

  // Expose globally
  window.EdmCartDrawer = { open, close };

  // ========================================
  // V3.5: ROBUST HEADER TITLE SHRINKING (Context7 Best Practices)
  // ========================================
  // Canvas-basierte Text-Messung (pixel-genau) + Binary Search (performant)
  // Verkleinert die Header-Überschrift dynamisch statt sie mit "..." abzuschneiden
  
  async function shrinkHeaderTitle() {
    const heading = document.querySelector('#CartDrawer .drawer__heading');
    if (!heading) return;
    
    const text = heading.textContent || heading.innerText;
    if (!text || !text.trim()) return;
    
    // CRITICAL: Wait for fonts to be loaded (prevents incorrect measureText)
    try {
      await document.fonts.ready;
    } catch(e) {
      console.warn('[Drawer] Font loading check failed:', e);
    }
    
    // CORRECT WIDTH CALCULATION: Use heading's own width, not parent's
    const headingRect = heading.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(heading);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    
    // Available width = total width - paddings - safety margin
    const maxWidth = headingRect.width - paddingLeft - paddingRight - 10;
    
    // CRITICAL: Validate maxWidth
    if (maxWidth <= 0) {
      console.warn('[Drawer] Invalid maxWidth:', maxWidth, '- Container too small or not visible');
      return; // Abort if invalid
    }
    
    // Canvas-based text measurement (Context7 Pattern)
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    let minSize = 10; // Lower minimum for very long text
    let maxSize = 20; // CSS-Default (--edm-font-title)
    let optimalSize = 20;
    
    // Binary Search for optimal font size
    while (maxSize - minSize > 0.5) {
      const testSize = (minSize + maxSize) / 2;
      
      // Font must match CSS exactly: 700 weight + Poppins
      context.font = `700 ${testSize}px 'Poppins', sans-serif`;
      const textWidth = context.measureText(text).width;
      
      if (textWidth <= maxWidth) {
        optimalSize = testSize;
        minSize = testSize;
      } else {
        maxSize = testSize;
      }
    }
    
    // Apply font size
    heading.style.fontSize = optimalSize + 'px';
    
    console.log('[Drawer V3.5] Header resized:', {
      text: text.substring(0, 30) + '...',
      fontSize: optimalSize.toFixed(1) + 'px',
      textWidth: context.measureText(text).width.toFixed(0) + 'px',
      maxWidth: maxWidth.toFixed(0) + 'px',
      headingWidth: headingRect.width.toFixed(0) + 'px',
      paddingLeft: paddingLeft.toFixed(0) + 'px',
      paddingRight: paddingRight.toFixed(0) + 'px'
    });
  }

  // Call on load and resize
  // V3.5: Async support with extended delay to ensure fonts are loaded
  window.addEventListener('load', () => {
    setTimeout(() => shrinkHeaderTitle(), 200);
  });
  window.addEventListener('resize', () => shrinkHeaderTitle());

  // Call when drawer opens (MutationObserver for data-edm-open attribute)
  const drawerObserver = new MutationObserver(() => {
    if (drawer && drawer.hasAttribute('data-edm-open')) {
      // Delay to ensure DOM is painted and fonts loaded
      setTimeout(() => shrinkHeaderTitle(), 100);
    }
  });
  
  if (drawer) {
    drawerObserver.observe(drawer, { 
      attributes: true, 
      attributeFilter: ['data-edm-open'] 
    });
  }

  // V3.4: Observer für Header Content Changes (z.B. nach Cart-Leerung)
  // CRITICAL: Beobachte den <h2> Tag DIREKT, da sich nur dessen textContent ändert
  const contentObserver = new MutationObserver(() => {
    setTimeout(shrinkHeaderTitle, 100);
  });
  if (drawer) {
    const heading = drawer.querySelector('.drawer__heading');
    if (heading) {
      contentObserver.observe(heading, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }
})();
